// src/hooks/useUserQueries.js
// Team / Assignees data hooks (React Query) — production-ready

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/axios';

/* --------------------------------- consts -------------------------------- */

const MIN_SEARCH_CHARS = 2;
const DEFAULT_LIMIT = 25;

/* ------------------------------- mappers -------------------------------- */

const toUserDTO = (u = {}) => ({
  id: String(u.id || u._id || ''),
  name: u.name || u.fullName || u.displayName || (u.email ? u.email.split('@')[0] : 'User'),
  email: u.email || '',
  role: u.role || 'staff',
  avatarUrl: u.avatarUrl || u.avatar || null,
  invited: !!u.invited,
  active: u.active !== false && u.softDeleted !== true,
});

const extractArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.users)) return d.users;
  return [];
};

/* --------------------------------- API ---------------------------------- */

/**
 * GET /users
 * Server expects: { search, role, includeInactive, includeInvited, page, limit }
 * NOTE: includeInvited defaults to true to avoid empty lists on fresh tenants.
 */
async function apiListUsers(
  params = {},
  { signal } = {}
) {
  const {
    search = '',
    role,
    includeInactive = false,
    includeInvited = true,
    page = 1,
    limit = DEFAULT_LIMIT,
  } = params;

  const res = await api.get('/users', {
    params: {
      search: String(search || '').trim(), // ✅ controller expects "search" (not "q")
      role,
      includeInactive: includeInactive ? 1 : 0,
      includeInvited: includeInvited ? 1 : 0,
      page,
      limit,
    },
    signal,
  });

  return extractArray(res).map(toUserDTO);
}

/**
 * GET /users/by-ids?ids=...
 */
async function apiUsersByIds(ids = [], { signal } = {}) {
  const uniq = Array.from(new Set((ids || []).map(String))).filter(Boolean);
  if (!uniq.length) return [];
  const res = await api.get('/users/by-ids', { params: { ids: uniq.join(',') }, signal });
  return extractArray(res).map(toUserDTO);
}

/**
 * POST /users/invite
 */
async function apiInviteUser(payload) {
  const res = await api.post('/users/invite', payload);
  return res.data;
}

/**
 * PATCH /users/:id/role
 */
async function apiUpdateUserRole({ id, role }) {
  const res = await api.patch(`/users/${id}/role`, { role });
  return res.data;
}

/**
 * DELETE /users/:id
 */
async function apiDeleteUser(id) {
  const res = await api.delete(`/users/${id}`);
  return res.data;
}

/* -------------------------------- queries ------------------------------- */

/**
 * Cached team list (initial/fallback).
 * Includes invited users by default so fresh tenants aren’t empty.
 */
export function useUsers(options = {}) {
  const {
    includeInvited = true,
    includeInactive = false,
    limit = DEFAULT_LIMIT,
    page = 1,
    role, // optional
    ...rqOpts
  } = options;

  return useQuery({
    queryKey: ['users', { search: '', includeInvited, includeInactive, limit, page, role }],
    queryFn: ({ signal }) =>
      apiListUsers({ search: '', includeInvited, includeInactive, limit, page, role }, { signal }),
    staleTime: 5 * 60 * 1000,
    ...rqOpts,
  });
}

/**
 * Typeahead search. Fires only when term length >= MIN_SEARCH_CHARS.
 * Defaults includeInvited=true so invited members appear in search results.
 */
export function useUserSearch(term, opts = {}) {
  const {
    limit = 10,
    includeInvited = true,
    includeInactive = false,
    role, // 'admin' | 'staff' | 'viewer' (optional)
    enabled = true,
    ...rqOpts
  } = opts;

  const q = String(term || '').trim();
  const canRun = enabled && q.length >= MIN_SEARCH_CHARS;

  return useQuery({
    queryKey: ['users', 'search', { q, role, includeInvited, includeInactive, limit }],
    queryFn: ({ signal }) =>
      apiListUsers({ search: q, role, includeInvited, includeInactive, limit }, { signal }),
    enabled: canRun,
    keepPreviousData: true,
    staleTime: 2 * 60 * 1000,
    ...rqOpts,
  });
}

/**
 * Hydrate existing assignees by ids to render chips/labels/avatars.
 */
export function useUsersByIds(ids = [], options = {}) {
  const keyIds = Array.from(new Set((ids || []).map(String))).filter(Boolean);

  return useQuery({
    queryKey: ['users', 'byIds', keyIds],
    queryFn: ({ signal }) => apiUsersByIds(keyIds, { signal }),
    enabled: keyIds.length > 0 && (options.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/* ------------------------------- mutations ------------------------------ */

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: apiInviteUser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUpdateUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: apiUpdateUserRole,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: apiDeleteUser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
